/**
 * Detail page density and text size dropdown selector.
 * Mirrors DensityDropdown.tsx structure for table density.
 */

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SlidersHorizontal, AlignJustify, List, LayoutList, AArrowUp } from "lucide-react";
import type { DetailDensity, DetailTextSize } from "@/lib/store/preferencesStore";

const DENSITY_OPTIONS: { value: DetailDensity; icon: typeof AlignJustify; label: string }[] = [
  { value: 'compact', icon: AlignJustify, label: 'Compact' },
  { value: 'default', icon: List, label: 'Default' },
  { value: 'spacious', icon: LayoutList, label: 'Spacious' },
];

const TEXT_SIZE_OPTIONS: { value: DetailTextSize; label: string; desc: string }[] = [
  { value: 'small', label: 'Small', desc: '12px' },
  { value: 'medium', label: 'Medium', desc: '14px' },
  { value: 'large', label: 'Large', desc: '15px' },
];

export function DetailDensityDropdown({
  density, setDensity, textSize, setTextSize,
}: {
  density: DetailDensity; setDensity: (d: DetailDensity) => void;
  textSize: DetailTextSize; setTextSize: (s: DetailTextSize) => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          className="h-9 w-9 rounded-full bg-card border border-border"
          aria-label="Detail display"
        >
          <SlidersHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        <div className="px-2 py-1.5 text-[11px] font-medium text-muted-foreground uppercase tracking-wider">Density</div>
        <DropdownMenuRadioGroup value={density} onValueChange={(v) => setDensity(v as DetailDensity)}>
          {DENSITY_OPTIONS.map(({ value, icon: Icon, label }) => (
            <DropdownMenuRadioItem key={value} value={value}>
              <Icon className="mr-2 h-4 w-4 text-muted-foreground" />
              {label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
        <div className="my-1 border-t border-border" />
        <div className="px-2 py-1.5 text-[11px] font-medium text-muted-foreground uppercase tracking-wider">Font size</div>
        <DropdownMenuRadioGroup value={textSize} onValueChange={(v) => setTextSize(v as DetailTextSize)}>
          {TEXT_SIZE_OPTIONS.map(({ value, label, desc }) => (
            <DropdownMenuRadioItem key={value} value={value}>
              <AArrowUp className="mr-2 h-4 w-4 text-muted-foreground" />
              <span>{label}</span>
              <span className="ml-auto text-[11px] text-muted-foreground">{desc}</span>
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
